import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  DeviceConnectionState,
  DeviceInfo,
  DeviceState,
  MeasurementProgress,
  MeasurementResult,
  DeviceError,
  DeviceEventHandlers,
} from '../types/device';

/**
 * Default device state when no device is connected
 */
const DEFAULT_DEVICE_STATE: DeviceState = {
  busy: false,
  progress: 0,
  electrodeDetected: false,
  electrodeType: 'LEGACY',
  temperature: 0,
  humidity: 0,
  batteryLevel: 0,
  batteryVoltage: 0,
  powerState: 'unknown',
  warnings: 0,
  pointsMax: null,
};

/**
 * Default measurement progress state
 */
const DEFAULT_MEASUREMENT_PROGRESS: MeasurementProgress = {
  isRunning: false,
  progress: 0,
};

/**
 * Device store state interface
 */
interface DeviceStoreState {
  // Connection state
  connectionState: DeviceConnectionState;

  // Connected device info (null when disconnected)
  deviceInfo: DeviceInfo | null;

  // Current device state from last message
  deviceState: DeviceState;

  // Active measurement progress
  measurementProgress: MeasurementProgress;

  // Last measurement result (until cleared)
  lastResult: MeasurementResult | null;

  // Current error (null when no error)
  error: DeviceError | null;

  // Set to true when a boot-time silent reconnect was attempted but failed
  reconnectAttemptFailed: boolean;

  // Event handlers for external subscriptions
  eventHandlers: DeviceEventHandlers;

  // ======= Actions =======

  /**
   * Set connection state (called by DeviceService)
   */
  setConnectionState: (state: DeviceConnectionState) => void;

  /**
   * Set connected device info
   */
  setDeviceInfo: (info: DeviceInfo | null) => void;

  /**
   * Update device state from DevMessage
   */
  updateDeviceState: (state: Partial<DeviceState>) => void;

  /**
   * Update measurement progress
   */
  updateMeasurementProgress: (progress: Partial<MeasurementProgress>) => void;

  /**
   * Set measurement result
   */
  setMeasurementResult: (result: MeasurementResult | null) => void;

  /**
   * Set error state
   */
  setError: (error: DeviceError | null) => void;

  /**
   * Clear error and return to previous state
   */
  clearError: () => void;

  /**
   * Reset store to disconnected state
   */
  reset: () => void;

  /**
   * Mark that a boot-time silent reconnect was attempted and failed
   */
  setReconnectAttemptFailed: (failed: boolean) => void;

  /**
   * Register event handlers
   */
  setEventHandlers: (handlers: DeviceEventHandlers) => void;

  // ======= Computed Helpers =======

  /**
   * Check if device is ready for measurement
   */
  isReadyForMeasurement: () => boolean;

  /**
   * Check if currently measuring
   */
  isMeasuring: () => boolean;

  /**
   * Check if device has low battery
   */
  hasLowBattery: () => boolean;

  /**
   * Get connection state as user-friendly string
   */
  getConnectionStateLabel: () => string;

  /**
   * Get a display label for the connected device (serial, uid or nickname)
   *
   * This is the same value shown in the AppBar chip; it prefers a user
   * editable nickname when available, falls back to serial number with
   * "Reader" prefix, then the UID, then a generic placeholder.
   */
  getDeviceLabel: () => string;

  /**
   * Returns true when the connection state indicates the device is not usable.
   * Matches both 'disconnected' and 'error' because DeviceService.handleDisconnect
   * calls setError() after setConnectionState('disconnected'), leaving the final
   * state as 'error'. All callers should use this instead of raw state comparisons.
   */
  isEffectivelyDisconnected: () => boolean;
}

/**
 * Initial state for the device store
 */
const initialState = {
  connectionState: 'disconnected' as DeviceConnectionState,
  deviceInfo: null,
  deviceState: DEFAULT_DEVICE_STATE,
  measurementProgress: DEFAULT_MEASUREMENT_PROGRESS,
  lastResult: null,
  error: null,
  reconnectAttemptFailed: false,
  eventHandlers: {},
};

/**
 * Device store for WebUSB state management
 */
export const useDeviceStore = create<DeviceStoreState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    ...initialState,

    // ======= Actions =======

    setConnectionState: (connectionState: DeviceConnectionState) => {
      const prev = get().connectionState;
      // Successful connection clears the boot-time failure flag
      set({ connectionState, ...(connectionState === 'connected' ? { reconnectAttemptFailed: false } : {}) });

      // Trigger event handlers
      const { eventHandlers, deviceInfo } = get();
      if (
        connectionState === 'connected' &&
        prev !== 'connected' &&
        deviceInfo
      ) {
        eventHandlers.onConnected?.(deviceInfo);
      } else if (
        connectionState === 'disconnected' &&
        prev !== 'disconnected'
      ) {
        eventHandlers.onDisconnected?.();
      }
    },

    setDeviceInfo: (deviceInfo: DeviceInfo | null) => {
      set({ deviceInfo });
    },

    updateDeviceState: (updates: Partial<DeviceState>) => {
      set((state) => ({
        deviceState: { ...state.deviceState, ...updates },
      }));

      // Trigger state update handler
      const { eventHandlers, deviceState } = get();
      eventHandlers.onStateUpdate?.(deviceState);
    },

    updateMeasurementProgress: (updates: Partial<MeasurementProgress>) => {
      const prev = get().measurementProgress;
      const next = { ...prev, ...updates };

      set({ measurementProgress: next });

      // Trigger progress handler
      const { eventHandlers } = get();
      eventHandlers.onMeasurementProgress?.(next);
    },

    setMeasurementResult: (result: MeasurementResult | null) => {
      set({
        lastResult: result,
        measurementProgress: DEFAULT_MEASUREMENT_PROGRESS,
      });

      // Trigger completion handler
      if (result) {
        const { eventHandlers } = get();
        eventHandlers.onMeasurementComplete?.(result);
      }
    },

    setError: (error: DeviceError | null) => {
      set({
        error,
        connectionState: error ? 'error' : get().connectionState,
      });

      // Trigger error handler
      if (error) {
        const { eventHandlers } = get();
        eventHandlers.onError?.(error);
      }
    },

    clearError: () => {
      const { deviceInfo } = get();
      set({
        error: null,
        connectionState: deviceInfo ? 'connected' : 'disconnected',
      });
    },

    reset: () => {
      set({
        ...initialState,
        eventHandlers: get().eventHandlers, // Preserve handlers
      });
    },

    setReconnectAttemptFailed: (failed: boolean) => {
      set({ reconnectAttemptFailed: failed });
    },

    setEventHandlers: (handlers: DeviceEventHandlers) => {
      set({ eventHandlers: handlers });
    },

    // ======= Computed Helpers =======

    isReadyForMeasurement: () => {
      const { connectionState, deviceState } = get();
      return (
        connectionState === 'connected' &&
        !deviceState.busy &&
        deviceState.electrodeDetected
      );
    },

    isMeasuring: () => {
      const { connectionState, measurementProgress } = get();
      return connectionState === 'busy' || measurementProgress.isRunning;
    },

    hasLowBattery: () => {
      const { deviceState } = get();
      return deviceState.batteryLevel < 20;
    },

    getConnectionStateLabel: () => {
      const { connectionState, error } = get();
      const labels: Record<DeviceConnectionState, string> = {
        disconnected: 'Disconnected',
        connecting: 'Connecting...',
        connected: 'Connected',
        busy: 'Measuring...',
        error: error?.message || 'Error',
      };
      return labels[connectionState];
    },

    isEffectivelyDisconnected: () => {
      const { connectionState } = get();
      return connectionState === 'disconnected' || connectionState === 'error';
    },

    getDeviceLabel: () => {
      const { deviceInfo } = get();
      if (!deviceInfo) return 'Connected';

      // nickname takes highest precedence
      if (deviceInfo.nickname && deviceInfo.nickname.trim() !== '') {
        return deviceInfo.nickname;
      }

      // fall back to human‑readable serial number
      if (deviceInfo.serialNumber && deviceInfo.serialNumber !== '0') {
        return `Reader ${deviceInfo.serialNumber}`;
      }

      // last option is UID (still better than nothing)
      if (deviceInfo.uid && deviceInfo.uid !== '00:00:00:00:00:00') {
        return deviceInfo.uid;
      }

      return 'Connected';
    },
  }))
);

// ======= Selectors for Performance =======

/**
 * Select only connection state (prevents unnecessary re-renders)
 */
export const selectConnectionState = (state: DeviceStoreState) =>
  state.connectionState;

/**
 * Select device info
 */
export const selectDeviceInfo = (state: DeviceStoreState) => state.deviceInfo;

/**
 * Select device state
 */
export const selectDeviceState = (state: DeviceStoreState) => state.deviceState;

/**
 * Select measurement progress
 */
export const selectMeasurementProgress = (state: DeviceStoreState) =>
  state.measurementProgress;

/**
 * Select battery info (for status bar)
 */
export const selectBatteryInfo = (state: DeviceStoreState) => ({
  level: state.deviceState.batteryLevel,
  voltage: state.deviceState.batteryVoltage,
  powerState: state.deviceState.powerState,
});

/**
 * Select error state
 */
export const selectError = (state: DeviceStoreState) => state.error;

/**
 * Select if device is ready for measurement
 */
export const selectIsReady = (state: DeviceStoreState) =>
  state.connectionState === 'connected' &&
  !state.deviceState.busy &&
  state.deviceState.electrodeDetected;

// ======= Selector Hooks for Common Use Cases =======

/**
 * Hook to get device connection state
 */
export const useConnectionState = () =>
  useDeviceStore(selectConnectionState);

/**
 * Hook to get device info
 */
export const useDeviceInfo = () =>
  useDeviceStore(selectDeviceInfo);

/**
 * Hook to get device state
 */
export const useDeviceState = () =>
  useDeviceStore(selectDeviceState);

/**
 * Hook to get measurement progress
 */
export const useMeasurementProgress = () =>
  useDeviceStore(selectMeasurementProgress);

/**
 * Hook to get battery info
 */
export const useBatteryInfo = () =>
  useDeviceStore(selectBatteryInfo);

/**
 * Hook to get error state
 */
export const useDeviceError = () =>
  useDeviceStore(selectError);

/**
 * Hook to check if device is ready for measurement
 */
export const useIsReady = () =>
  useDeviceStore(selectIsReady);

// Export type for external use
export type { DeviceStoreState };
